import type { CSSProperties, ReactNode } from 'react';
import './page-header.css';

export interface PageHeaderProps {
  title: string;
  subtitle?: ReactNode;
  /** Range tabs, an Add button, a filter chip — whatever the view puts there. */
  actions?: ReactNode;
}

/**
 * Page title, subtitle and the view's own controls.
 *
 * A real `h1`. The prototype used a styled `p`, which leaves a page with no heading at
 * all for anyone navigating by landmark.
 */
export function PageHeader({ title, subtitle, actions }: PageHeaderProps) {
  return (
    <header className="pageHeader">
      <div className="pageHeaderTitles">
        <h1 className="pageHeaderTitle">{title}</h1>
        {subtitle ? <p className="pageHeaderSubtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="pageHeaderActions">{actions}</div> : null}
    </header>
  );
}

export interface MobileBarProps {
  logoSrc: string;
  label: string;
  onOpenNav: () => void;
  menuIcon: ReactNode;
}

/** Shown below --bp-drawer, where the sidebar has become a drawer. */
export function MobileBar({ logoSrc, label, onOpenNav, menuIcon }: MobileBarProps) {
  return (
    <div className="mobileBar">
      <button
        type="button"
        className="mobileBarToggle"
        onClick={onOpenNav}
        aria-label="Open navigation"
      >
        {menuIcon}
      </button>
      <img src={logoSrc} alt="" width={24} height={24} />
      <span className="mobileBarLabel">{label}</span>
    </div>
  );
}

export interface AssumeBarProps {
  /** "Viewing as Bodyline". */
  label: string;
  onQuit?: () => void;
  icon?: ReactNode;
  style?: CSSProperties;
}

/** Fixed bottom-centre pill shown while a super admin views a partner's portal. */
export function AssumeBar({ label, onQuit, icon, style }: AssumeBarProps) {
  return (
    <div className="assumeBar" role="status" aria-live="polite" style={style}>
      <span className="assumeBarLabel">
        {icon ? <span aria-hidden="true">{icon}</span> : null}
        {label}
      </span>
      {onQuit ? (
        <button type="button" className="assumeBarQuit" onClick={onQuit}>
          Quit
        </button>
      ) : null}
    </div>
  );
}

export interface ImpersonationBarProps {
  partnerName: string;
  /** Retained for compatibility; the design-system bar does not render this value. */
  expiresAt: string;
  onQuit: () => void;
  eyeIcon: ReactNode;
}

/** Compatibility wrapper for the design-system AssumeBar primitive. */
export function ImpersonationBar({ partnerName, onQuit, eyeIcon }: ImpersonationBarProps) {
  return <AssumeBar label={`Viewing as ${partnerName}`} onQuit={onQuit} icon={eyeIcon} />;
}
